import { useCallback, useState } from "react";
import { useReactFlow } from "reactflow";
import { FlowStepData } from "src/types/flow";
import useFlowClipboardStore from "../hooks/useFlowClipboardStore";
import EdgeContextMenu from "./EdgeContextMenu";
import NodeContextMenu from "./NodeContextMenu";

interface ContextMenuPosition {
  x: number;
  y: number;
}

interface ContextMenuContainerProps {
  children: React.ReactNode;
  onPasteStep: (params: {
    edgeId: string;
    stepId: string;
    step?: FlowStepData;
  }) => void;
}

function ContextMenuContainer({
  children,
  onPasteStep,
}: ContextMenuContainerProps) {
  const [menuState, setMenuState] = useState<{
    position: ContextMenuPosition | null;
    nodeId: string | null;
    edgeId: string | null;
  }>({
    position: null,
    nodeId: null,
    edgeId: null,
  });
  const { copiedStep } = useFlowClipboardStore();

  const { getEdges } = useReactFlow();

  const closeMenu = useCallback(() => {
    setMenuState({
      position: null,
      nodeId: null,
      edgeId: null,
    });
  }, []);

  const findEdgeId = useCallback((element: Element): string | null => {
    const edgeId = element.getAttribute("data-id");
    if (edgeId) return edgeId;
    const selectors = ["path[id]", "g[id]", "[id]"];
    for (const selector of selectors) {
      const el = element.querySelector(selector);
      if (el) {
        const id = el.getAttribute("id");
        if (id) return id;
      }
    }
    return null;
  }, []);

  const isValidEdgeType = useCallback(
    (edgeId: string): boolean => {
      const edge = getEdges().find((e) => e.id === edgeId);
      return edge?.type
        ? edge.type.includes("add_") ||
            edge.type === "step_edge" ||
            edge.type === "fork_step_edge"
        : false;
    },
    [getEdges]
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      closeMenu();

      const nodeElement = (event.target as Element).closest(
        ".react-flow__node"
      );
      if (nodeElement) {
        const nodeId = nodeElement.getAttribute("data-id");
        if (nodeId) {
          setMenuState({
            position: { x: event.clientX, y: event.clientY },
            nodeId,
            edgeId: null,
          });
          return;
        }
      }

      const edgeElement = (event.target as Element).closest(
        ".react-flow__edge"
      );
      if (edgeElement) {
        const edgeId = findEdgeId(edgeElement);
        if (edgeId && isValidEdgeType(edgeId)) {
          setMenuState({
            position: { x: event.clientX, y: event.clientY },
            nodeId: null,
            edgeId,
          });
        }
      }
    },
    [findEdgeId, isValidEdgeType, closeMenu]
  );

  return (
    <div onContextMenu={handleContextMenu} className="w-full h-full">
      {children}

      {menuState.position && menuState.nodeId && (
        <NodeContextMenu
          nodeId={menuState.nodeId}
          top={menuState.position.y}
          left={menuState.position.x}
          onClose={closeMenu}
        />
      )}

      {menuState.position && menuState.edgeId && (
        <EdgeContextMenu
          edgeId={menuState.edgeId}
          top={menuState.position.y}
          left={menuState.position.x}
          onClose={closeMenu}
          onPasteStep={onPasteStep}
          copiedStep={copiedStep}
        />
      )}
    </div>
  );
}

export default ContextMenuContainer;
